import type { CoastalLocationConfig, CoastalSceneState } from './coastal-types';
import { solarPosition, utcFromLocal } from '../solar';
import type { SurfBreakDirection } from './surf-break-direction';

export type SurfBiome = 'polzeath' | 'temperate' | 'tropical' | 'mediterranean' | 'volcanic' | 'nordic' | 'arid' | 'subtropical' | 'pacific';
export type SurfSceneLocation = CoastalLocationConfig & {
  name: string;
  country: string;
  region: string;
  lat: number;
  lng: number;
  timezone: string | null;
  biome?: SurfBiome;
  breakDirection?: SurfBreakDirection;
};

export const POLZEATH_SURF_LOCATION: SurfSceneLocation = {
  slug: 'polzeath', name: 'Polzeath', country: 'United Kingdom', region: 'Cornwall',
  lat: 50.575, lng: -4.917, timezone: 'Europe/London', beachFacingDeg: 280,
  beachGradient: 'gentle', breakType: 'beach', exposedSectors: [[230, 330]], shelterLevel: 'semi-sheltered',
};

export const SURF_BIOMES = {
  polzeath: { label: 'Polzeath-inspired coast', backdrop: 'backdrop', sand: '211,198,160' },
  temperate: { label: 'Temperate coast', backdrop: 'temperate', sand: '211,198,160' },
  tropical: { label: 'Tropical coast', backdrop: 'tropical', sand: '230,218,179' },
  mediterranean: { label: 'Dry Mediterranean coast', backdrop: 'mediterranean', sand: '224,207,168' },
  volcanic: { label: 'Volcanic coast', backdrop: 'volcanic', sand: '76,80,77' },
  nordic: { label: 'Northern coast', backdrop: 'nordic', sand: '174,179,171' },
  subtropical: { label: 'Subtropical woodland coast', backdrop: 'coasts/cove-subtropical', sand: '226,208,167' },
  pacific: { label: 'Cool maritime forest coast', backdrop: 'coasts/cove-pacific', sand: '206,202,180' },
  arid: { label: 'Arid coast', backdrop: 'arid', sand: '218,184,129' },
} as const;

/** Broad art defaults from catalogue geography, never a surveyed view of a beach. */
export function surfBiomeForLocation(location: SurfSceneLocation): SurfBiome {
  if (location.biome && location.biome in SURF_BIOMES) return location.biome;
  if (location.slug === 'polzeath') return 'polzeath';
  const { country, lat, lng } = location;
  if (country === 'Spain' && lat > 26 && lat < 30 && lng < -12 && lng > -19) return 'volcanic';
  if (Math.abs(lat) >= 55 || ['Norway', 'Iceland'].includes(country)) return 'nordic';
  if (['Morocco', 'Namibia', 'Oman', 'United Arab Emirates', 'Peru'].includes(country)
    || (country === 'Chile' && lat > -30)) return 'arid';
  if (country === 'Mexico' && lng < -109 && lat > 22) return 'arid';
  if (country === 'New Zealand') return 'pacific';
  if (country === 'Australia' && lng > 145 && lat < -23 && lat > -34) return 'subtropical';
  if (country === 'Australia' && lng < 120 && lat > -27) return 'arid';
  if (country === 'Australia' && lng < 120 && lat <= -27) return 'mediterranean';
  if (Math.abs(lat) <= 25) return 'tropical';
  if (['Italy', 'Greece', 'Croatia', 'Turkey', 'Cyprus', 'Malta'].includes(country)
    || (country === 'France' && lng > 2 && lat < 44)
    || (country === 'Spain' && lng > -2 && lat < 43)
    || (country === 'Portugal' && lat < 40)) return 'mediterranean';
  return 'temperate';
}

export function validSurfTimezone(value: string | null | undefined): string | null {
  if (!value) return null;
  try { new Intl.DateTimeFormat('en-GB', { timeZone: value }).format(0); return value; }
  catch { return null; }
}

export function surfLocalDate(location: SurfSceneLocation, now = new Date()): string {
  const timezone = validSurfTimezone(location.timezone);
  if (timezone) return new Intl.DateTimeFormat('en-CA', { timeZone: timezone }).format(now);
  const offset = Number.isFinite(location.lng) ? Math.round(location.lng / 15) : 0;
  return new Date(now.getTime() + offset * 3600000).toISOString().slice(0, 10);
}

export function surfLocalHour(location: SurfSceneLocation, now = new Date()): number {
  const timezone = validSurfTimezone(location.timezone);
  if (timezone) return Number(new Intl.DateTimeFormat('en-GB', { timeZone: timezone, hour: '2-digit', hourCycle: 'h23' }).format(now));
  const offset = Number.isFinite(location.lng) ? Math.round(location.lng / 15) : 0;
  return new Date(now.getTime() + offset * 3600000).getUTCHours();
}

export function surfLocalTime(location: SurfSceneLocation, now = new Date()): number {
  const timezone = validSurfTimezone(location.timezone);
  const minute = timezone ? Number(new Intl.DateTimeFormat('en-GB', { timeZone: timezone, minute: '2-digit' }).format(now)) : now.getUTCMinutes();
  return surfLocalHour(location, now) + minute / 60;
}

export function surfInstantAt(location: SurfSceneLocation, date: string, hour: number): Date | null {
  const calendar = new Date(`${date}T12:00:00Z`);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !Number.isFinite(calendar.getTime()) || calendar.toISOString().slice(0, 10) !== date || !Number.isFinite(hour)
    || hour < 0 || hour > 24 || !Number.isFinite(location.lat) || Math.abs(location.lat) > 90
    || !Number.isFinite(location.lng) || Math.abs(location.lng) > 180) return null;
  const timezone = validSurfTimezone(location.timezone);
  const instant = timezone ? utcFromLocal(date, hour, timezone)
    : new Date(new Date(`${date}T00:00:00Z`).getTime() + (hour - Math.round(location.lng / 15)) * 3600000);
  return Number.isFinite(instant.getTime()) ? instant : null;
}

/** Use the existing solar model once per time selection, never in the animation loop. */
export function surfSunPosition(location: SurfSceneLocation, date: string, hour: number) {
  const instant = surfInstantAt(location, date, hour);
  return instant ? solarPosition(location.lat, location.lng, instant) : null;
}

export function surfSunElevation(location: SurfSceneLocation, date: string, hour: number): number | null {
  return surfSunPosition(location, date, hour)?.elevationDeg ?? null;
}

/** Small tidal ranges and steep beaches move less than a broad, gently shelving beach. */
export function surfTidePosition(state: CoastalSceneState, location: SurfSceneLocation): number {
  if (state.tide.source === 'neutral') return 0.5;
  const gradient = location.beachGradient === 'steep' ? 0.35 : location.beachGradient === 'moderate' ? 0.65 : 1;
  const range = state.tide.rangeM === undefined ? 1 : Math.min(1, Math.max(0, state.tide.rangeM) / 4);
  return 0.5 + (state.tide.position01 - 0.5) * gradient * range;
}
